import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { AccessPolicy, Reference, Resource } from '@medplum/fhirtypes';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

import { TooltipProvider } from '@eluve/components';

import { FhirList } from '../components/FhirList';
import { buildDefaultRegistry } from '../views/default-registry';

import { useEditScopeMode } from './FhirEditScope';
import { CYCLE_ALREADY_SHOWN } from './copy';
import {
  type StubReason,
  classifyReference,
  referenceStubLabel,
} from './reference';
import type { FhirComponentRegistry } from './registry';
import {
  RenderContextProvider,
  childRenderContext,
  isInParentChain,
  rootRenderContext,
  useRenderContext,
  viewRenderContext,
} from './render-context';
import { type ResolutionMap, lookupReference } from './resolution-map';
import type {
  RenderContext,
  RenderPlacement,
  RenderSurface,
  SlotItems,
  Slots,
} from './view-contract';

type RendererEnv = {
  registry: FhirComponentRegistry;
  map: ResolutionMap;
  accessPolicy?: AccessPolicy;
};

const RendererEnvContext = createContext<RendererEnv | undefined>(undefined);

function useRendererEnv(): RendererEnv {
  const env = useContext(RendererEnvContext);
  if (!env) {
    throw new Error(
      '[fhir-renderer] RenderResource must be used inside <FhirRenderRoot>',
    );
  }
  return env;
}

type RendererConfig = {
  registry: FhirComponentRegistry;
  surface: RenderSurface;
  debug: boolean;
  /** FHIR GraphQL Apollo client, when the app runs one — see useFhirRendererClient. */
  client?: ApolloClient<NormalizedCacheObject>;
};

const RendererConfigContext = createContext<RendererConfig | undefined>(
  undefined,
);

let defaultRegistry: FhirComponentRegistry | undefined;

export type FhirRendererProviderProps = {
  /** Component registry — code, app-wide. Defaults to the built-in registry. */
  registry?: FhirComponentRegistry;
  surface?: RenderSurface;
  debug?: boolean;
  /**
   * The app's FHIR GraphQL Apollo client. Held here so anything under the
   * provider knows its GraphQL context (via useFhirRendererClient) without
   * per-component wiring.
   */
  client?: ApolloClient<NormalizedCacheObject>;
  children: ReactNode;
};

/**
 * App-level renderer configuration: WHICH components render each resource
 * type. Mount once near the app root; every FhirResourceScope below inherits
 * it. Registry is code (changes on deploy) — distinct from the resolution
 * map (data, changes per fetch), which lives on FhirResourceScope.
 */
export function FhirRendererProvider({
  registry,
  surface = 'page',
  debug = false,
  client,
  children,
}: FhirRendererProviderProps) {
  const value = useMemo(
    () => ({
      registry: registry ?? (defaultRegistry ??= buildDefaultRegistry()),
      surface,
      debug,
      client,
    }),
    [registry, surface, debug, client],
  );
  return (
    <RendererConfigContext.Provider value={value}>
      {/* One tooltip provider for every view below — field hints and any
          other SimpleTooltip work without the app mounting its own. Nesting
          under <Layout>'s provider is harmless (Radix allows it). */}
      <TooltipProvider>{children}</TooltipProvider>
    </RendererConfigContext.Provider>
  );
}

/**
 * The FHIR GraphQL client the renderer provider was configured with, or
 * undefined when the app has none (not initialized yet, feature disabled).
 */
export function useFhirRendererClient():
  | ApolloClient<NormalizedCacheObject>
  | undefined {
  const config = useContext(RendererConfigContext);
  if (!config) {
    throw new Error(
      '[fhir-renderer] useFhirRendererClient must be used inside <FhirRendererProvider>',
    );
  }
  return config.client;
}

export type FhirResourceScopeProps = {
  /** Resolution map — the resources this scope's query fetched. */
  map: ResolutionMap;
  accessPolicy?: AccessPolicy;
  /** Override the provider's surface/debug for this scope only. */
  surface?: RenderSurface;
  /** UI placement of this render tree — per-site by nature, so declared here, not on the provider. */
  placement?: RenderPlacement;
  debug?: boolean;
  children: ReactNode;
};

/**
 * Data-level render scope: WHAT resources are in hand to join references
 * against. Mount next to the query that fetched them; RenderResource works
 * anywhere inside.
 */
export function FhirResourceScope({
  map,
  accessPolicy,
  surface,
  placement,
  debug,
  children,
}: FhirResourceScopeProps) {
  const config = useContext(RendererConfigContext);
  if (!config) {
    throw new Error(
      '[fhir-renderer] FhirResourceScope must be used inside <FhirRendererProvider>',
    );
  }
  const env = useMemo(
    () => ({ registry: config.registry, map, accessPolicy }),
    [config.registry, map, accessPolicy],
  );
  // 'edit' when an active FhirEditScope encloses this scope.
  const mode = useEditScopeMode();
  const ctx = useMemo(
    () =>
      rootRenderContext({
        surface: surface ?? config.surface,
        placement,
        mode,
        debug: debug ?? config.debug,
      }),
    [surface, placement, mode, debug, config.surface, config.debug],
  );
  return (
    <RendererEnvContext.Provider value={env}>
      <RenderContextProvider value={ctx}>{children}</RenderContextProvider>
    </RendererEnvContext.Provider>
  );
}

export type RenderResourceProps = {
  resource: Resource;
  /** Set by the walker when this node came from a Reference[] slot. */
  inList?: boolean;
};

/**
 * The walker. Per node: registry lookup, view context (placement facts for
 * this node) + child context (self pushed onto the chain, for slots), slot
 * construction, then render the view. Recursion rides React's own render —
 * slots are element descriptors, and only placed slots mount.
 */
export function RenderResource({
  resource,
  inList = false,
}: RenderResourceProps) {
  const { registry, map, accessPolicy } = useRendererEnv();
  const parentCtx = useRenderContext();

  const viewCtx = useMemo(
    () =>
      viewRenderContext({ parent: parentCtx, resource, accessPolicy, inList }),
    [parentCtx, resource, accessPolicy, inList],
  );
  const childCtx = useMemo(
    () => childRenderContext(parentCtx, resource, accessPolicy),
    [parentCtx, resource, accessPolicy],
  );
  const { slots, slotItems } = useMemo(
    () => buildSlots(resource, map, childCtx),
    [resource, map, childCtx],
  );

  const View = registry.resolve(resource);
  if (!View) return null;

  return (
    <RenderContextProvider value={childCtx}>
      <View data={resource} slots={slots} slotItems={slotItems} ctx={viewCtx} />
    </RenderContextProvider>
  );
}

/**
 * Reference discovery is duck-typed on the instance (a top-level field whose
 * value carries a `reference` string, or a list of them). Typed slot names
 * come from ReferenceKeys<T> at compile time; this is the runtime side.
 */
function isReferenceLike(value: unknown): value is Reference {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof (value as Reference).reference === 'string'
  );
}

const NON_SLOT_KEYS = new Set([
  'contained',
  'meta',
  'text',
  'extension',
  'modifierExtension',
]);

function buildSlots(
  resource: Resource,
  map: ResolutionMap,
  ctx: RenderContext,
): { slots: Slots<Resource>; slotItems: SlotItems<Resource> } {
  const slots: Record<string, ReactNode> = {};
  const slotItems: Record<string, ReactNode[]> = {};
  for (const [key, value] of Object.entries(resource)) {
    if (NON_SLOT_KEYS.has(key)) continue;
    if (isReferenceLike(value)) {
      const item = renderTarget({ ref: value, map, ctx, key });
      slots[key] = item;
      slotItems[key] = [item];
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every(isReferenceLike)
    ) {
      const items = value.map((ref, index) =>
        renderTarget({ ref, map, ctx, key: `${key}-${index}`, inList: true }),
      );
      // Default container; parents re-container via slotItems.
      slots[key] = <FhirList>{items}</FhirList>;
      slotItems[key] = items;
    }
  }
  return {
    slots: slots as Slots<Resource>,
    slotItems: slotItems as SlotItems<Resource>,
  };
}

function renderTarget(input: {
  ref: Reference;
  map: ResolutionMap;
  ctx: RenderContext;
  key: string;
  inList?: boolean;
}): ReactNode {
  const { ref, map, ctx, key, inList } = input;
  const target = lookupReference(map, ref);
  if (!target) return <ReferenceStub key={key} reference={ref} />;
  if (isInParentChain(ctx, target)) {
    return (
      <span key={key} data-fhir-cycle="">
        {CYCLE_ALREADY_SHOWN}
      </span>
    );
  }
  return <RenderResource key={key} resource={target} inList={inList} />;
}

function stubReason(ref: Reference): StubReason {
  const classified = classifyReference(ref);
  if (classified.kind === 'relative' || classified.kind === 'contained') {
    return 'dangling';
  }
  if (classified.kind === 'empty') return 'empty';
  return 'unfetchable';
}

/** Unresolvable reference: neutral display-text stub — never an error, never a leak. */
function ReferenceStub({ reference }: { reference: Reference }) {
  return (
    <span data-fhir-stub="">
      {referenceStubLabel(reference, stubReason(reference))}
    </span>
  );
}
